import { apiClient, ApiClient } from './client';
import type { PersonaListResponse, PersonaDetailResponse, CreatePersonaPayload, UpdatePersonaPayload } from './types';

class Persona {
  public readonly id: string;
  private readonly apiClient: ApiClient;
  public display_name: string;
  public icon: string;
  public description: string;
  public is_default: boolean;

  constructor(id: string, apiClient: ApiClient, details: PersonaDetailResponse) {
    this.id = id;
    this.apiClient = apiClient;
    this.display_name = details.display_name;
    this.icon = details.icon;
    this.description = details.description;
    this.is_default = details.is_default;
  }

  async delete(): Promise<void> {
    try {
      await this.apiClient.delete(`/personas/${this.id}/`);
    } catch (error) {
      console.error('Failed to delete persona:', error);
      throw error;
    }
  }

  async makeDefault(): Promise<void> {
    try {
      await this.apiClient.post(`/personas/${this.id}/make-default/`);
    } catch (error) {
      console.error('Failed to make persona default:', error);
      throw error;
    }
  }

  async update(data: UpdatePersonaPayload): Promise<Persona> {
    try {
      const formData = new FormData();
      if (data.display_name) formData.append('display_name', data.display_name);
      if (data.icon) formData.append('icon', data.icon);
      if (data.description) formData.append('description', data.description);

      const response = await this.apiClient.patch(`/personas/${this.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return new Persona(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to update persona:', error);
      throw error;
    }
  }

  async get(): Promise<Persona> {
    try {
      const response = await this.apiClient.get(`/personas/${this.id}/`);
      return new Persona(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch persona details:', error);
      throw error;
    }
  }
}

export const personas = {
  async list(): Promise<PersonaListResponse[]> {
    try {
      const response = await apiClient.get('/personas/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch personas:', error);
      throw error;
    }
  },

  async owned(): Promise<PersonaListResponse[]> {
    try {
      const response = await apiClient.get('/personas/', {
        params: { owned: true }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch owned personas:', error);
      throw error;
    }
  },

  async search(query: string): Promise<PersonaListResponse[]> {
    try {
      const response = await apiClient.get('/personas/', {
        params: { search: query }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search personas:', error);
      throw error;
    }
  },

  async create(data: CreatePersonaPayload): Promise<Persona> {
    try {
      const formData = new FormData();
      formData.append('display_name', data.display_name);
      if (data.icon) {
        formData.append('icon', data.icon);
      }
      formData.append('description', data.description);

      const response = await apiClient.post('/personas/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return new Persona(response.data.id, apiClient, response.data);
    } catch (error) {
      console.error('Failed to create persona:', error);
      throw error;
    }
  },

  async get(id: string): Promise<Persona> {
    try {
      const response = await apiClient.get(`/personas/${id}/`);
      return new Persona(id, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch persona details:', error);
      throw error;
    }
  }
};

export type { Persona as PersonaType };